/**
 * Hooks del catálogo de proyectos (TanStack Query).
 *
 * Clave de la caché: ['proyectos']. Cuando cambia el nombre o el enlace también se refrescan las
 * tareas, porque cada una trae el nombre y el enlace de su proyecto.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ActualizarProyectoEntrada } from '@cdtec-backlog/contrato';
import { actualizarProyecto, crearProyecto, eliminarProyecto, listarProyectos } from './api';

export function useProyectos() {
  return useQuery({
    queryKey: ['proyectos'],
    queryFn: listarProyectos,
    staleTime: 60_000,
  });
}

function useRefrescarProyectos() {
  const cliente = useQueryClient();
  return (incluirTareas: boolean) => {
    void cliente.invalidateQueries({ queryKey: ['proyectos'] });
    if (incluirTareas) {
      void cliente.invalidateQueries({ queryKey: ['tareas'] });
      void cliente.invalidateQueries({ queryKey: ['tarea'] });
    }
  };
}

/** Sin aviso propio: quien llama decide qué mostrar (por ejemplo, el 409 de duplicado no es un error). */
export function useCrearProyecto() {
  const refrescar = useRefrescarProyectos();
  return useMutation({
    mutationFn: crearProyecto,
    onSuccess: () => refrescar(false),
  });
}

export function useActualizarProyecto() {
  const refrescar = useRefrescarProyectos();
  return useMutation({
    mutationFn: ({ id, cambios }: { id: number; cambios: ActualizarProyectoEntrada }) =>
      actualizarProyecto(id, cambios),
    onSuccess: (_proyecto, { cambios }) =>
      refrescar(cambios.nombre !== undefined || cambios.enlaceRepo !== undefined),
  });
}

export function useEliminarProyecto() {
  const refrescar = useRefrescarProyectos();
  return useMutation({
    mutationFn: eliminarProyecto,
    onSuccess: () => refrescar(false),
  });
}
